import { Text } from "@hackthenorth/north";
import { GraphQLError } from "graphql";
import { Button as ButtonNew, Spinner, TextArea, TextInput } from "north.js";
import React, { useEffect, useState } from "react";
import styled from "styled-components";

import { Icon, TagInput } from "src/shared/components";
import { errorToast } from "src/shared/components/Toast";
import { useHackerContext, useNetworkingContext } from "src/shared/contexts";
import { Field } from "src/shared/contexts/HackerContext/types";
import { useHackerState } from "src/shared/contexts/HackerContext/useHackerState";
import * as Networking from "src/shared/contexts/NetworkingContext/types";

import { ACCEPTED_FIELDS, VALIDATORS } from "./constants";

const Profile = () => {
  const maxTagAmount = 7;
  let interestsSplit: string[] = [];

  const { responsesState, setResponsesState, isValid } = useHackerState(
    ACCEPTED_FIELDS,
    VALIDATORS
  );

  const {
    extendedProfile,
    updateExtendedProfile,
    isLoading: isExtendedProfileLoading,
    refetchExtendedProfile,
  } = useNetworkingContext();

  const [localExtendedProfile, setLocalExtendedProfile] =
    useState<Partial<Networking.ExtendedProfile> | null>(extendedProfile);
  if (localExtendedProfile?.interests) {
    interestsSplit = localExtendedProfile?.interests?.split(",");
  }
  const Options: string[] = interestsSplit;
  const [addTag, setAddTag] = useState(false);
  const [prevTags, setPrevTags] = useState<string[]>(interestsSplit);
  const [maxCharError, setMaxCharError] = useState<string[]>([]);

  const setExtendedProfile = (field: string, value: string) => {
    setLocalExtendedProfile((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const [isEditing, setIsEditing] = useState(false);

  const { updateResponses, isLoading } = useHackerContext();

  const isPageLoading = isLoading || isExtendedProfileLoading;

  useEffect(() => {
    if (!isPageLoading) {
      if (localExtendedProfile !== extendedProfile) {
        setLocalExtendedProfile(extendedProfile);
      }
    }
    // eslint-disable-next-line
  }, [extendedProfile]);

  const onSave = async () => {
    if (isValid) {
      try {
        const responses = {
          ...responsesState,
        };

        const { errors } = await updateResponses(responses);

        if (errors) {
          throw new Error(
            "We weren't able to submit your answers, please try again."
          );
        }

        const { errors: networkingDataError } = await updateExtendedProfile(
          localExtendedProfile
        );

        if (networkingDataError) {
          throw new Error(
            "We weren't able to submit your interests, please try again."
          );
        }
        refetchExtendedProfile?.();
      } catch (e) {
        if (typeof e === "object") {
          errorToast((e as GraphQLError)?.message);
        } else {
          errorToast((e as GraphQLError)?.message);
        }
      }
    }
  };

  const splitPronouns = new RegExp("\\s*,\\s*");

  const splitInterests = () => {
    let joinArrayOfInterests;
    if (localExtendedProfile?.interests) {
      const arrayOfInterests = localExtendedProfile.interests.split(",");
      joinArrayOfInterests = arrayOfInterests.join(", ");
    }
    return joinArrayOfInterests;
  };

  return (
    <>
      <ProfileHeaderWrapper>
        <ProfileHeader>
          <Text mods="heading h3 bold">Profile information</Text>
          {!isEditing && (
            <ButtonNew
              color="tertiary"
              size="md"
              onClick={() => setIsEditing(true)}
            >
              Edit <Icon name="edit" size="h3" />
            </ButtonNew>
          )}
        </ProfileHeader>
        {isEditing && (
          <Text>This info will appear on your public profile.</Text>
        )}
      </ProfileHeaderWrapper>
      {isPageLoading ? (
        <Spinner />
      ) : (
        <>
          <InputRow>
            <HalfCol>
              <Text mods="heading h4 bold">Preferred Name</Text>
              {isEditing ? (
                <FullInput
                  size="md"
                  placeholder={`Enter your preferred name`}
                  error={
                    !isValid ? "This field cannot be left empty" : undefined
                  }
                  value={responsesState.rsvp_preferred_name || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setResponsesState(Field.PREFERRED_NAME, e.target.value);
                  }}
                />
              ) : (
                <Text>{responsesState.rsvp_preferred_name}</Text>
              )}
            </HalfCol>
            <HalfCol>
              <Text mods="heading h4 bold">Pronouns</Text>
              {isEditing ? (
                <FullInput
                  size="md"
                  maxLength={50}
                  placeholder={`Enter your preferred pronouns`}
                  value={
                    responsesState.rsvp_preferred_pronouns?.join(", ") || ""
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setResponsesState(
                      Field.PRONOUNS,
                      e.target.value.trim().split(splitPronouns)
                    );
                  }}
                />
              ) : (
                <Text>
                  {responsesState.rsvp_preferred_pronouns?.join(", ")}
                </Text>
              )}
            </HalfCol>
          </InputRow>
          <Row>
            <Text mods="heading h4 bold">Biography</Text>
            {isEditing ? (
              <TextArea
                fullWidth
                maxLength={200}
                placeholder={`Enter your biography`}
                value={responsesState.networking_biography || ""}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                  setResponsesState(Field.BIOGRAPHY, e.target.value);
                }}
              />
            ) : (
              <Text>{responsesState.networking_biography}</Text>
            )}
          </Row>
          <Row>
            <Text mods="heading h4 bold">Interests</Text>
            {isEditing ? (
              <>
                <Text>Add up to 7 interests.</Text>
                <TagWrapper>
                  <TagInput
                    multi
                    max={maxTagAmount}
                    adding={addTag}
                    setAdding={setAddTag}
                    options={Options}
                    selectedOptions={Options}
                    setSelectedOptions={(newSelection) => {
                      if (
                        newSelection[newSelection.length - 1] &&
                        newSelection[newSelection.length - 1].length > 20
                      ) {
                        setMaxCharError([
                          "Please enter 20 characters or less.",
                        ]);
                      } else {
                        setMaxCharError([]);
                        const joinedInterests = newSelection.join(",");
                        if (interestsSplit) {
                          setPrevTags(interestsSplit);
                        }
                        setExtendedProfile(Field.INTERESTS, joinedInterests);
                      }
                    }}
                    canAdd={Options.length < maxTagAmount}
                    errors={maxCharError}
                  />
                </TagWrapper>
                <TagDescriptionWrapper>
                  {maxCharError.length > 0 && (
                    <Text mods="error">{maxCharError}</Text>
                  )}
                  <Text mods="caption grey">
                    Ex. Building robots, playing basketball, hiking
                  </Text>
                </TagDescriptionWrapper>
              </>
            ) : (
              <Text>{splitInterests()}</Text>
            )}
            {isEditing && (
              <ButtonWrapper>
                <ButtonNew
                  color="secondary"
                  size="lg"
                  onClick={() => {
                    setIsEditing(false);
                    setExtendedProfile(Field.INTERESTS, prevTags.join(","));
                  }}
                >
                  Cancel
                </ButtonNew>
                <ButtonNew
                  color="primary"
                  size="lg"
                  onClick={() => {
                    onSave();
                    setIsEditing(false);
                    setPrevTags(Options);
                  }}
                  disabled={!isValid}
                >
                  Confirm
                </ButtonNew>
              </ButtonWrapper>
            )}
          </Row>
        </>
      )}
    </>
  );
};

const ProfileHeaderWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProfileHeader = styled.div`
  display: flex;
  justify-content: space-between;
`;

const InputRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin: 24px 0 36px 0;

  @media (max-width: 768px) {
    flex-direction: column;
    margin-bottom: 12px;
  }
`;

const Row = styled.div`
  display: block;
  margin: 24px 0 36px 0;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }
`;

const TagWrapper = styled.div`
  display: flex;
  background: #f3f4f6;
  width: 100%;
  align-items: center;
  border-radius: 8px;
  padding-left: 8px;
`;

const TagDescriptionWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const HalfCol = styled.div`
  width: 45%;
  @media (max-width: 768px) {
    width: 100%;
    margin: 12px 0;
  }
`;

const FullInput = styled(TextInput)`
  width: 100%;
`;

const ButtonWrapper = styled.div`
  display: flex;
  margin-top: 24px;
  column-gap: 10px;
`;

export default Profile;
